import { useSearchParams } from 'react-router';
import { Skeleton } from '@awj/ui';

import { PageTitle } from '~/components/PageTitle.tsx';
import { AuditPanel } from '~/components/settings/AuditPanel.tsx';
import { SettingsGroup } from '~/components/settings/SettingsGroup.tsx';
import { StaffPanel } from '~/components/settings/StaffPanel.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useSettings } from '~/lib/settings-queries.ts';
import { useSession } from '~/lib/session.tsx';

const PANELS = ['shop', 'staff', 'audit'] as const;

type Panel = (typeof PANELS)[number];

/**
 * Settings, staff and the audit trail.
 *
 * SuperAdmin only, and refused at the API as well as hidden from the menu — this is where
 * payment credentials, SMTP passwords and access to the shop itself live.
 *
 * The setting groups are all on one panel rather than one panel each: they are read far more
 * often than they are changed, and hunting through six tabs for "free shipping threshold" is
 * worse than scrolling one page.
 */
export function SettingsPage() {
  const { t } = useI18n();
  const { has } = useSession();
  const [params, setParams] = useSearchParams();

  const raw = params.get('panel');
  const panel: Panel = PANELS.includes(raw as Panel) ? (raw as Panel) : 'shop';

  const settings = useSettings();

  if (!has('SuperAdmin')) {
    return (
      <>
        <PageTitle title={t('nav.settings')} />
        <div className="empty-panel">
          {/*
            Said plainly rather than shown an empty page full of 403s. The menu already hides
            this, so anyone here typed the address.
          */}
          <p>{t('settings.superAdminOnly')}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('nav.settings')} />

      <nav className="section-nav" aria-label={t('nav.settings')}>
        {PANELS.map((name) => (
          <button
            key={name}
            type="button"
            className={`section-nav__item ${panel === name ? 'is-selected' : ''}`}
            aria-current={panel === name ? 'true' : undefined}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('panel', name);
              setParams(next, { replace: true });
            }}
          >
            {t(`settings.${name}` as 'settings.shop')}
          </button>
        ))}
      </nav>

      {panel === 'shop' ? (
        settings.isPending ? (
          <Skeleton height="420px" />
        ) : settings.isError ? (
          <div className="empty-panel">
            <p>{t('common.somethingWentWrong')}</p>
          </div>
        ) : (
          <div className="settings-stack">
            {settings.data.map((group) => (
              <SettingsGroup key={group.group} group={group} />
            ))}
          </div>
        )
      ) : null}

      {panel === 'staff' ? <StaffPanel /> : null}
      {panel === 'audit' ? <AuditPanel /> : null}
    </>
  );
}
